import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Home() {
  useScrollAnimation();

  useEffect(() => {
    document.title = 'Audacious Quest PTE Ltd | Audaciously Analytical';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative h-[420px] md:h-[600px] overflow-hidden scroll-animate">
        <img
          src="https://readdy.ai/api/search-image?query=Stunning%20panoramic%20aerial%20view%20of%20modern%20metropolitan%20city%20skyline%20at%20golden%20hour%20with%20dramatic%20cloud%20formations%2C%20towering%20glass%20skyscrapers%20reflecting%20warm%20amber%20sunlight%2C%20vast%20urban%20landscape%20stretching%20to%20the%20horizon%2C%20cinematic%20wide%20angle%20photography%20with%20deep%20atmospheric%20perspective%20and%20warm%20neutral%20tones&width=1600&height=600&seq=hero-home-v2&orientation=landscape"
          alt="Global city skyline at golden hour"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white text-center drop-shadow-lg px-4">
            Audaciously Analytical
          </h2>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <p className="text-sm text-foreground-500 mb-2 tracking-wide">Our Approach</p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-primary-900 mb-6">
            Investments Boldly Redefined
          </h2>
          <p className="text-foreground-700 max-w-3xl leading-relaxed text-sm md:text-base">
            We are able to create a distinctive perspective on a company that offers globally broad
            portfolios of technology-related enterprises by combining an analysis of the company
            fundamentals with forward-looking trends.
          </p>
        </div>
      </section>

      {/* Market Awareness */}
      <section className="scroll-animate">
        <div className="flex flex-col md:flex-row">
          <div className="w-full md:w-1/2">
            <img
              src="https://readdy.ai/api/search-image?query=Double%20exposure%20silhouette%20of%20businessman%20in%20elegant%20suit%20with%20glowing%20holographic%20city%20skyline%20and%20financial%20data%20overlay%2C%20futuristic%20technology%20meets%20urban%20business%20world%2C%20teal%20and%20amber%20light%20tones%2C%20cinematic%20corporate%20photography%20with%20high%20contrast&width=700&height=450&seq=home-market&orientation=landscape"
              alt="Market awareness"
              className="w-full h-64 md:h-[450px] object-cover object-top"
            />
          </div>
          <div className="w-full md:w-1/2 flex items-center bg-background-100 px-6 md:px-12 lg:px-16 py-12 md:py-0">
            <p className="text-lg md:text-xl lg:text-2xl text-foreground-800 leading-relaxed max-w-md font-heading">
              We are aware of how critical it is to react promptly to market developments in order to
              safeguard and increase your investment.
            </p>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-14 md:py-20 px-4 md:px-6 scroll-animate">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading text-primary-900 mb-8 md:mb-10">
            Discover More
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {/* About Us */}
            <Link to="/about" className="group block">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Two%20business%20executives%20in%20formal%20dark%20suits%20engaged%20in%20focused%20strategic%20discussion%20at%20sleek%20modern%20conference%20table%2C%20warm%20side%20lighting%20through%20floor-to-ceiling%20windows%2C%20corporate%20consultancy%20concept%2C%20cinematic%20editorial%20photography%20with%20shallow%20depth%20of%20field&width=500&height=320&seq=home-service-consultancy&orientation=landscape"
                  alt="About Us"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900 mb-2">About Us</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                Learn more about our alternative viewpoint on investments and how we build trust through deep market expertise.
              </p>
            </Link>

            {/* Strategy */}
            <Link to="/strategy" className="group block">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Silhouette%20of%20executive%20standing%20before%20large%20curved%20digital%20screens%20displaying%20glowing%20market%20data%20and%20analytics%2C%20strategic%20decision%20making%20concept%2C%20dark%20moody%20office%20with%20warm%20amber%20accent%20lighting%2C%20cinematic%20corporate%20editorial%20photography&width=500&height=320&seq=home-service-strategy&orientation=landscape"
                  alt="Strategy"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900 mb-2">Strategy</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                We identify undervalued prospects and determine future winners through meticulous analysis and forward-looking insights.
              </p>
            </Link>

            {/* Contact */}
            <Link to="/contact" className="group block">
              <div className="overflow-hidden mb-4 rounded-lg">
                <img
                  src="https://readdy.ai/api/search-image?query=Close%20up%20of%20professional%20trader%20hands%20on%20keyboard%20with%20multiple%20monitor%20screens%20displaying%20candlestick%20charts%20and%20financial%20data%2C%20dark%20trading%20desk%20atmosphere%20with%20warm%20monitor%20glow%2C%20cinematic%20corporate%20editorial%20photography%20with%20moody%20lighting&width=500&height=320&seq=home-service-trading&orientation=landscape"
                  alt="Contact"
                  className="w-full h-52 md:h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <h3 className="text-lg font-medium text-primary-900 mb-2">Contact</h3>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                For any enquiries or questions, please reach out to us here.
              </p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}